import api from "./api";
import fileDownload from "js-file-download";

// Gestão de boletos — as três perguntas do cliente, respondidas pela mesma
// regra que o calendário usa (core: Service/BolepixGestaoSql.cs).
//
// A régua da data MUDA por situação, e a tela precisa dizer isso em texto:
//   A_VENCER / VENCIDO -> data de VENCIMENTO do boleto
//   PAGO               -> data do CRÉDITO no extrato (é quando o dinheiro caiu)
export const SITUACAO = {
  A_VENCER: "avencer",
  VENCIDO: "vencidos",
  PAGO: "pagos",
} as const;

export type Situacao = (typeof SITUACAO)[keyof typeof SITUACAO];

export interface LinhaGestao {
  situacao: string;
  id: string;
  idLote: string | null;
  parcela: number | null;
  idCliente: number;
  numeroDocumento: string | null;
  idTransacao: string | null;
  dataEmissao: string | null;
  dataVencimento: string | null;
  /** Dia do crédito no extrato. Só em PAGO. */
  dataCredito: string | null;
  /** PAGO: dias até o crédito. VENCIDO: dias de atraso. A_VENCER: dias que faltam. */
  dias: number | null;
  faixaAtraso: number;
  valor: number;
  valorLiquido: number | null;
  statusTransacao: string | null;
  emitido: boolean;
  estornado: boolean;
  nomePagador: string | null;
  cpfPagador: string | null;
  emailPagador: string | null;
  numeroPagador: string | null;
  linhaDigitavel: string | null;
  barcode: string | null;
}

export interface ResumoGestao {
  qtdeAVencer: number;
  valorAVencer: number;
  qtdeVencido: number;
  valorVencido: number;
  qtdePago: number;
  valorPago: number;
  valorLiquidoPago: number;
  qtdeEstornado: number;
  valorEstornado: number;
  /** Do dinheiro que entrou na janela: quanto veio de cada origem de vencimento. */
  qtdeRecebidoDoPeriodo: number;
  valorRecebidoDoPeriodo: number;
  qtdeRecebidoEmAtraso: number;
  valorRecebidoEmAtraso: number;
  qtdeRecebidoAdiantado: number;
  valorRecebidoAdiantado: number;
  qtdeNaoEmitida: number;
  valorNaoEmitido: number;
}

export interface Janela {
  de: string;
  ate: string;
}

export interface JanelasResumo {
  aVencer: Janela;
  vencido: Janela;
  pago: Janela;
}

export interface AnaliticoGestao {
  de: string;
  ate: string;
  serie: Array<{
    data: string;
    qtdePrevista: number;
    valorPrevisto: number;
    qtdeRecebida: number;
    valorRecebido: number;
    valorLiquidoRecebido: number;
  }>;
  aging: Array<{ faixa: number; rotulo: string; qtde: number; valor: number }>;
  devedores: Array<{
    cpfPagador: string | null;
    nomePagador: string | null;
    qtdeVencida: number;
    valorVencido: number;
    maiorAtraso: number | null;
  }>;
  indicadores: {
    qtdeVencidaNoPeriodo: number;
    valorVencidoNoPeriodo: number;
    qtdePagaDoPeriodo: number;
    valorPagoDoPeriodo: number;
    diasAteCreditoMedio: number | null;
    ticketMedio: number | null;
    taxaConversao: number | null;
  };
}

interface Periodo {
  de?: string;
  ate?: string;
}

function params(p: Record<string, any>) {
  const q = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.append(k, String(v));
  });
  return q.toString();
}

export default class BolepixGestaoAPI {
  /**
   * Contadores do topo. Somados no servidor — a página tem 15 linhas.
   *
   * SEM período, cada bloco usa a janela natural dele (a vencer: 30 dias à
   * frente; vencido: tudo em aberto; pago: mês corrente) e o servidor devolve
   * quais foram, em `janelas`. É assim que a tela abre — com uma janela só
   * para os três, o card de vencidos aparecia zerado enquanto a aba em foco
   * era "a vencer", que olha para o futuro.
   */
  static getResumo = async (
    periodo: Periodo = {},
    busca?: string,
    incluirNaoEmitidos = false
  ): Promise<{ janelas: JanelasResumo; resumo: ResumoGestao }> => {
    const { data } = await api.get(
      `/BolepixGestao/resumo?${params({ ...periodo, busca, incluirNaoEmitidos })}`
    );
    return data;
  };

  static getLista = async (
    situacao: Situacao,
    periodo: Periodo = {},
    page = 1,
    recordsPerPage = 15,
    busca?: string,
    incluirNaoEmitidos = false,
    incluirEstornados = false
  ): Promise<{
    situacao: string;
    de: string;
    ate: string;
    page: number;
    recordsPerPage: number;
    totalRegistros: number;
    totalPaginas: number;
    registros: LinhaGestao[];
  }> => {
    const { data } = await api.get(
      `/BolepixGestao/lista?${params({
        situacao,
        ...periodo,
        page,
        recordsPerPage,
        busca,
        incluirNaoEmitidos,
        incluirEstornados,
      })}`
    );
    return data;
  };

  static getAnalitico = async (
    periodo: Periodo = {},
    topDevedores = 10
  ): Promise<AnaliticoGestao> => {
    const { data } = await api.get(
      `/BolepixGestao/analitico?${params({ ...periodo, topDevedores })}`
    );
    return data;
  };

  /** XLSX da lista inteira (não da página). */
  static exportar = async (
    situacao: Situacao,
    periodo: Periodo = {},
    busca?: string,
    incluirNaoEmitidos = false,
    incluirEstornados = false
  ) => {
    const resp = await api.get(
      `/BolepixGestao/exportar?${params({
        situacao,
        ...periodo,
        busca,
        incluirNaoEmitidos,
        incluirEstornados,
      })}`,
      { responseType: "blob" }
    );

    const nome = `bolepix-${situacao}-${periodo.de ?? "inicio"}-${periodo.ate ?? "hoje"}.xlsx`;
    fileDownload(resp.data, nome);
  };
}
